import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowUpRight, BadgeCheck, CheckCircle2, ChevronDown,
  Clock3, MapPin, MessageCircle, Phone, ShieldCheck,
} from "lucide-react";
import { SERVICE_PAGES } from "../data/services";
import { CONTACT_PHONE, CONTACT_PHONE_DISPLAY, CONTACT_ADDRESS, CONTACT_HOURS, WHATSAPP_LINK } from "../data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const PROCESS = [
  ["01", "Arıza kaydı", "Telefon, WhatsApp veya form ile arızayı anlatın."],
  ["02", "Yerinde teşhis", "Mobil ekibimiz gelir, cihazı gözünüzün önünde test eder."],
  ["03", "Net fiyat & onay", "Fiyat işlem öncesi söylenir; onaysız işlem yapılmaz."],
  ["04", "Garantili teslim", "Parça ve işçilik 1 yıl Nova Teknik garantisinde."],
];

export default function ServicePage({ slug }) {
  const service = SERVICE_PAGES.find((s) => s.slug === slug);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!service) return;
    document.title = service.metaTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", service.meta);
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  const others = SERVICE_PAGES.filter((s) => s.slug !== slug);

  return (
    <div className="site-shell" data-testid="service-page">
      <div className="noise-overlay" aria-hidden="true" />

      <header className="sp-header">
        <div className="container header-inner">
          <Link to="/" className="brand" data-testid="sp-brand-link">
            <img className="brand-logo" src="/logo.png" alt="Nova Teknik Servis Düzce" />
          </Link>
          <div className="header-actions">
            <span className="status-badge"><span className="pulse-dot" /> AÇIK · 08:00–20:00</span>
            <a className="btn-call" data-testid="sp-call-button" href={`tel:${CONTACT_PHONE}`}>
              <Phone size={15} /> <span className="call-text">{CONTACT_PHONE_DISPLAY}</span>
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="sp-hero container">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <Link to="/" className="sp-back" data-testid="sp-back-home-link"><ArrowLeft size={14} /> Tüm hizmetler</Link>
            <div className="kicker">{service.tag}</div>
            <h1 className="sp-h1">{service.h1}</h1>
            <p className="hero-lede">{service.desc}</p>
            <div className="hero-actions">
              <a className="btn-primary" data-testid="sp-request-link" href={`tel:${CONTACT_PHONE}`}>
                <Phone size={16} /> Hemen Ara
              </a>
              <a className="btn-ghost" data-testid="sp-whatsapp-button" href={WHATSAPP_LINK} target="_blank" rel="noreferrer">
                <MessageCircle size={16} /> WhatsApp'tan Yaz
              </a>
            </div>
          </motion.div>
          <motion.div className="hero-visual" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}>
            <div className="hero-frame">
              <span className="corner tl" /><span className="corner tr" /><span className="corner bl" /><span className="corner br" />
              <img src={service.image} alt={service.h1} data-testid="sp-image" />
              <div className="hero-badge">
                <BadgeCheck size={20} />
                <span><b>1 yıl garanti</b><small>orijinal parça</small></span>
              </div>
              <div className="hero-tag">NOVA TEKNİK · DÜZCE MERKEZ</div>
            </div>
          </motion.div>
        </section>

        <section className="sp-faults container" data-testid="sp-faults-section">
          <motion.div className="section-head" initial="hidden" whileInView="show" viewport={{ once: true, margin: "-70px" }} variants={fadeUp}>
            <div>
              <div className="kicker">SIK ÇÖZDÜĞÜMÜZ ARIZALAR</div>
              <h2>Bu sorunlardan biri mi var?</h2>
            </div>
            <p>Listede olmayan bir arıza için de bizi arayabilirsiniz.</p>
          </motion.div>
          <div className="fault-grid">
            {service.faults.map((f, i) => (
              <motion.div className="fault-item" key={f} data-testid={`sp-fault-${i}`} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i % 2}>
                <CheckCircle2 size={17} /> {f}
              </motion.div>
            ))}
          </div>
        </section>

        <section className="sp-process container" data-testid="sp-process-section">
          <div className="kicker">SÜREÇ NASIL İŞLİYOR?</div>
          <div className="manifesto-grid">
            {PROCESS.map(([num, title, text], i) => (
              <motion.div key={num} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <div data-testid={`sp-process-${i}`} className="sp-process-card">
                  <span className="chapter-num">{num}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="sp-faq container" data-testid="sp-faq-section">
          <div className="kicker">SIK SORULANLAR</div>
          <div className="faq-list">
            {service.faqs.map(([q, a], i) => (
              <div className={`faq-item ${openFaq === i ? "active" : ""}`} key={q}>
                <button data-testid={`sp-faq-button-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                  <span className="qnum">0{i + 1}</span>{q}<ChevronDown size={18} />
                </button>
                {openFaq === i && <p data-testid={`sp-faq-answer-${i}`}>{a}</p>}
              </div>
            ))}
          </div>
        </section>

        <section className="container">
          <div className="sp-cta" data-testid="sp-cta-band">
            <div>
              <h2>Cihazınızı bugün onaralım.</h2>
              <p>Düzce Merkez ve tüm mahallelerine aynı gün servis.</p>
            </div>
            <div className="hero-actions">
              <Link className="btn-primary" data-testid="sp-cta-form-link" to="/#servis-talebi">
                Servis Talebi Oluştur <ArrowUpRight size={16} />
              </Link>
              <a className="btn-ghost" data-testid="sp-cta-call-link" href={`tel:${CONTACT_PHONE}`}>
                <Phone size={16} /> {CONTACT_PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </section>

        <section className="container sp-related" data-testid="sp-related-section">
          <div className="kicker">DİĞER HİZMETLERİMİZ</div>
          <div className="local-chips">
            {others.map((s, i) => (
              <Link key={s.slug} to={`/${s.slug}`} data-testid={`sp-related-${i}`}>{s.title.toUpperCase()}</Link>
            ))}
          </div>
        </section>
      </main>

      <footer className="sp-footer" data-testid="sp-footer">
        <div className="container sp-footer-inner">
          <span><MapPin size={14} /> {CONTACT_ADDRESS}</span>
          <span><Clock3 size={14} /> {CONTACT_HOURS}</span>
          <a href={`tel:${CONTACT_PHONE}`} data-testid="sp-footer-phone"><Phone size={14} /> {CONTACT_PHONE_DISPLAY}</a>
          <span><ShieldCheck size={14} /> 1 yıl işçilik & parça garantisi</span>
        </div>
      </footer>

      <a className="float-whatsapp" data-testid="float-whatsapp-button" href={WHATSAPP_LINK} target="_blank" rel="noreferrer" aria-label="WhatsApp">
        <MessageCircle size={22} />
      </a>
    </div>
  );
}
